import { useEffect, useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { getMoveDurationSeconds } from '../../utils/animation'
import {
  BOARD_TOP_Y,
  MARBLE_RADIUS,
  MARBLE_Y,
  boardToXZ,
  getMarblePalette,
  positionKey,
} from './constants'
import { createMarbleCoreTexture } from './textures'
import type { Position } from '../../stores/gameStore'
import type { MarbleStyleId } from './constants'

interface MarblePieceProps {
  position: Position
  player: number
  marbleStyle: MarbleStyleId
  selected: boolean
  movePath?: Position[]
  onClick: () => void
}

const HOP_HEIGHT = MARBLE_RADIUS * 1.5
const SHADOW_Y = BOARD_TOP_Y + 0.02
const CORE_RADIUS = MARBLE_RADIUS * 0.66

function hashPosition(pos: Position, player: number) {
  return ((pos.q + 17) * 73856093) ^ ((pos.r + 17) * 19349663) ^ (player * 83492791)
}

const easeInOut = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t)

export default function MarblePiece({
  position,
  player,
  marbleStyle,
  selected,
  movePath,
  onClick,
}: MarblePieceProps) {
  const groupRef = useRef<THREE.Group>(null)
  const liftRef = useRef<THREE.Group>(null)
  const squashRef = useRef<THREE.Group>(null)
  const spinRef = useRef<THREE.Group>(null)
  const shadowRef = useRef<THREE.Mesh>(null)
  const causticRef = useRef<THREE.Mesh>(null)
  const startedAt = useRef(performance.now())
  const spinAxis = useRef(new THREE.Vector3(1, 0, 0))

  const palette = getMarblePalette(marbleStyle, player)
  const coreTexture = useMemo(
    () => createMarbleCoreTexture(palette, hashPosition(position, player)),
    [palette, position, player],
  )

  const pathXZ = useMemo(
    () => (movePath && movePath.length > 1 ? movePath.map((pos) => boardToXZ(pos)) : []),
    [movePath],
  )
  const pathKey = movePath?.map(positionKey).join('|') ?? positionKey(position)

  useEffect(() => {
    startedAt.current = performance.now()
    if (pathXZ.length > 1) {
      const first = pathXZ[0]
      const last = pathXZ[pathXZ.length - 1]
      // Roll about the horizontal axis perpendicular to the travel direction.
      spinAxis.current.set(-(last.z - first.z), 0, last.x - first.x).normalize()
    }
  }, [pathKey, pathXZ])

  useEffect(() => () => coreTexture.dispose(), [coreTexture])

  useFrame((state) => {
    const group = groupRef.current
    const lift = liftRef.current
    const squash = squashRef.current
    const spin = spinRef.current
    const shadow = shadowRef.current
    const caustic = causticRef.current
    if (!group || !lift || !squash || !spin || !shadow || !caustic) return

    const shadowMat = shadow.material as THREE.MeshBasicMaterial
    const causticMat = caustic.material as THREE.MeshBasicMaterial

    if (pathXZ.length > 1) {
      const duration = getMoveDurationSeconds(movePath)
      const elapsed = (performance.now() - startedAt.current) / 1000
      const progress = THREE.MathUtils.clamp(elapsed / duration, 0, 1)
      const segments = pathXZ.length - 1
      const raw = progress * segments
      const segment = Math.min(Math.floor(raw), segments - 1)
      const localT = raw - segment
      const from = pathXZ[segment]
      const to = pathXZ[segment + 1]
      const eased = easeInOut(localT)

      group.position.set(from.x + (to.x - from.x) * eased, 0, from.z + (to.z - from.z) * eased)

      const arc = Math.sin(localT * Math.PI)
      lift.position.y = MARBLE_Y + arc * HOP_HEIGHT
      // Squash on contact, stretch at the apex.
      const sy = THREE.MathUtils.lerp(0.82, 1.06, arc)
      const sxz = THREE.MathUtils.lerp(1.12, 0.97, arc)
      squash.scale.set(sxz, sy, sxz)

      const totalSpin = (Math.hypot(pathXZ[segments].x - pathXZ[0].x, pathXZ[segments].z - pathXZ[0].z) / MARBLE_RADIUS) * 0.6
      spin.setRotationFromAxisAngle(spinAxis.current, progress * totalSpin)

      shadow.scale.setScalar(1 + arc * 0.9)
      shadowMat.opacity = 0.26 - arc * 0.16
      caustic.scale.setScalar(1 + arc * 0.6)
      causticMat.opacity = 0.2 - arc * 0.1
      return
    }

    const base = boardToXZ(position)
    group.position.set(base.x, 0, base.z)
    spin.rotation.set(0, 0, 0)

    if (selected) {
      const bob = 0.5 + 0.5 * Math.sin(state.clock.elapsedTime * 3)
      lift.position.y = MARBLE_Y + bob * 0.08
      squash.scale.setScalar(1.08)
      shadow.scale.setScalar(1.12)
      shadowMat.opacity = 0.22 - bob * 0.05
      caustic.scale.setScalar(1.2 + bob * 0.1)
      causticMat.opacity = 0.24
    } else {
      lift.position.y = MARBLE_Y
      squash.scale.setScalar(1)
      shadow.scale.setScalar(1)
      shadowMat.opacity = 0.26
      caustic.scale.setScalar(1)
      causticMat.opacity = 0.16
    }
  })

  return (
    <group
      ref={groupRef}
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
    >
      {/* Soft contact shadow */}
      <mesh ref={shadowRef} position={[0, SHADOW_Y, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[MARBLE_RADIUS * 0.92, 40]} />
        <meshBasicMaterial color="#1c0f06" transparent opacity={0.26} depthWrite={false} />
      </mesh>

      {/* Coloured caustic cast onto the wood */}
      <mesh ref={causticRef} position={[0, SHADOW_Y + 0.004, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[MARBLE_RADIUS * 0.82, 44]} />
        <meshBasicMaterial
          color={palette.caustic}
          transparent
          opacity={0.16}
          depthWrite={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>

      <group ref={liftRef} position={[0, MARBLE_Y, 0]}>
        <group ref={squashRef}>
          <group ref={spinRef}>
            {/* Opaque swirl core suspended inside the glass */}
            <mesh>
              <sphereGeometry args={[CORE_RADIUS, 36, 24]} />
              <meshStandardMaterial
                map={coreTexture}
                roughness={0.42}
                metalness={0}
                emissive={palette.core}
                emissiveIntensity={0.12}
                envMapIntensity={0.35}
              />
            </mesh>

            {/* Glass shell */}
            <mesh castShadow>
              <sphereGeometry args={[MARBLE_RADIUS, 54, 38]} />
              <meshPhysicalMaterial
                color={palette.base}
                roughness={0.05}
                metalness={0}
                transmission={0.32}
                thickness={MARBLE_RADIUS * 1.4}
                ior={1.52}
                clearcoat={1}
                clearcoatRoughness={0.04}
                attenuationColor={palette.base}
                attenuationDistance={1.4}
                emissive={palette.core}
                emissiveIntensity={0.1}
                envMapIntensity={1.4}
                transparent
              />
            </mesh>

            {/* Crisp catch-light */}
            <mesh position={[-MARBLE_RADIUS * 0.34, MARBLE_RADIUS * 0.52, MARBLE_RADIUS * 0.3]}>
              <sphereGeometry args={[MARBLE_RADIUS * 0.09, 12, 12]} />
              <meshBasicMaterial color="#fffaf0" transparent opacity={0.7} />
            </mesh>
          </group>
        </group>
      </group>
    </group>
  )
}
